import { Monitor, Moon, Sun } from 'lucide-react';
import { useThemeStore } from '@/store/theme-store';
import type { ThemePreference } from '@/lib/theme';

/**
 * Colour theme control. Rendered in the top right of every screen — the login card's header and the
 * authenticated shell's — so it is reachable without a session and without leaving the current page.
 *
 * Cycles light → dark → system. `system` is a state of its own rather than an inferred default: a
 * provider who wants the portal to follow their machine needs to be able to say so after having picked
 * a fixed theme once.
 */
const NEXT: Record<ThemePreference, ThemePreference> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

const ICONS: Record<ThemePreference, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const LABELS: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

export function ThemeToggle(): React.JSX.Element {
  const preference = useThemeStore((state) => state.preference);
  const setPreference = useThemeStore((state) => state.setPreference);

  const next = NEXT[preference];
  const Icon = ICONS[preference];

  return (
    <button
      type="button"
      onClick={(): void => {
        setPreference(next);
      }}
      // The accessible name carries both the current state and what the press will do: the icon alone
      // cannot say which of the three the portal is on (WCAG 1.1.1, 4.1.2).
      aria-label={`Theme: ${LABELS[preference]}. Switch to ${LABELS[next].toLowerCase()}.`}
      title={`Theme: ${LABELS[preference]}`}
      className="text-muted hover:text-gold-800 hover:bg-surface-strong inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors"
    >
      <Icon aria-hidden="true" className="h-4 w-4" />
    </button>
  );
}
